import { handleError } from 'common/api';
import { compressFileDownload, getSignedUrl } from 'modules/common/slice';

import { substringByByte, validateByteLength } from './common';

interface CustDoc {
  CUST_CLEAR_REQ_NO?: string;
  HBL_NO?: string;
  CRE_SYS_ID?: string;
  DOC_ID: string;
  FILE_NM: string;
}

interface CustData {
  CUST_CLEAR_REQ_NO: string;
  ENT_CD: string;
  CONTRACT_NO: string;
}

const ZIP_FILE_NAME_PREFIX = 'ait_tuukanfiles';
const CONTRACT_NO_BYTE_LIMIT = 100;

export const downloadFileByUrl = (fileUrl: string) => {
  try {
    if (!fileUrl) {
      return;
    }
    const link = document.createElement('a');
    const fileUrlSplitted = fileUrl.split('/');

    link.href = fileUrl;
    link.setAttribute('download', fileUrlSplitted[fileUrlSplitted.length - 1]);

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link?.parentNode?.removeChild(link);
  } catch (err) {
    handleError(err);
  }
};

export const viewPdfFile = (fileUrl: string) => {
  try {
    if (!fileUrl) {
      return;
    }

    window.open(fileUrl);
  } catch (err) {
    handleError(err);
  }
};

/**
 * 添付ファイルの実態チェックのため内容を取得する
 *
 * @param file
 * @returns 削除済み、空ファイルなどは空文字を返却する
 */
export const fetchAsText = async (file: File) => {
  return new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = (e: any) => {
      // 読み込んだ結果を resolve する
      resolve(e.currentTarget.result);
    };
    fr.onerror = () => {
      // ファイルの実態が存在しない場合、空文字を返却
      resolve('');
    };
    // ファイル内容を読込む
    fr.readAsText(file);
  });
};

/**
 * CUST/HBLの主キーを保持しているかによりS3パスのキー情報を返却する
 * @param doc 通関依頼ドキュメント
 * @returns
 */
const createKeyPath = (doc: CustDoc) => {
  if (doc.HBL_NO && doc.CRE_SYS_ID) return `${doc.HBL_NO}/${doc.CRE_SYS_ID}`;
  return doc.CUST_CLEAR_REQ_NO || '';
};

/**
 * 通関依頼ファイルダウンロード（1ファイル）
 *
 * @param doc 通関依頼ドキュメント
 * @returns
 */
export const callGetSignedUrlForCustDoc = (doc: CustDoc) => {
  const keyPath = createKeyPath(doc);
  // ファイルダウンロード
  return callGetSignedUrl(keyPath, doc.DOC_ID, doc.FILE_NM, process.env.REACT_APP_CCR_S3_DIRECTORY || '');
};

/**
 * 通関依頼(輸出)ファイルダウンロード（1ファイル）
 *
 * @param doc 通関依頼ドキュメント
 * @returns
 */
export const callGetSignedUrlForCustExpDoc = (doc: CustDoc) => {
  const keyPath = createKeyPath(doc);
  // ファイルダウンロード
  return callGetSignedUrl(keyPath, doc.DOC_ID, doc.FILE_NM, process.env.REACT_APP_CCR_EXPORT_S3_DIRECTORY || '');
};

/**
 * チャットファイルダウンロード（1ファイル）
 * @param chatRoomId
 * @param messageId
 * @param messageBody
 * @returns
 */
export const callGetSignedUrlForChat = (chatRoomId: string, messageId: number, messageBody: string) => {
  // ファイルダウンロード
  return callGetSignedUrl(chatRoomId, String(messageId), messageBody, process.env.REACT_APP_CHAT_S3_DIRECTORY || '');
};

/**
 * ファイルダウンロード（1ファイル）
 *
 * @param key S3パス（データKey）
 * @param id S3パス（ID）
 * @param fileName ファイル名
 * @returns
 */
const callGetSignedUrl = (key: string, id: string, fileName: string, s3Prefix: string) => {
  return getSignedUrl({
    filePath: `${s3Prefix}/${key}/${id}`,
    fileName: fileName,
    upload: '0',
  });
};

/**
 * 一括ファイルダウンロードAPI呼び出し
 *
 * @param downloadFileList ダウンロードファイルリスト
 * @param custData ダウンロード対象の通関依頼データ
 * @param userId ユーザID
 * @param authority 権限
 * @param screenId 画面ID
 * @param isUpdateDownloadFlg ダウンロードした通関依頼のDLフラグを更新するか（オプション：デフォルトON）
 * @param isMultipleCustNoFlg 複数の通関依頼に跨るダウンロードか（オプション：デフォルトOFF）
 * @param isExport 輸出側のファイルダウンロードか（オプション：デフォルトOFF）
 * @returns
 */
export const callCompressFileDownload = (
  downloadFileList: (CustDoc & CustData)[],
  custData: CustData,
  userId: string,
  authority: { SCREEN_ID: string; MENU_RESOURCE_ID: string }[],
  screenId: string,
  isUpdateDownloadFlg: boolean = true,
  isMultipleCustNoFlg: boolean = false,
  isExport: boolean = false
) => {
  const fileList = downloadFileList.map((record) => {
    const s3Prefix = isExport ? process.env.REACT_APP_CCR_EXPORT_S3_DIRECTORY : process.env.REACT_APP_CCR_S3_DIRECTORY;
    const keyPath = createKeyPath(record);
    // 検索結果画面からの一括ダウンロードは複数のCustNoでのダウンロードが必要
    const keyInfo = isMultipleCustNoFlg ? record : custData;
    const escapeContractNo = keyInfo.CONTRACT_NO.replace(/\//g, ' ');
    const substringContNo = substringByByte(escapeContractNo, CONTRACT_NO_BYTE_LIMIT);
    const isOverByteLimitContNo = !validateByteLength(escapeContractNo, CONTRACT_NO_BYTE_LIMIT);
    const byteLimitContNo = `${substringContNo}${isOverByteLimitContNo ? '...' : ''}`;
    return {
      s3Path: `${s3Prefix}/${keyPath}/${record.DOC_ID}`,
      zipPath: `${ZIP_FILE_NAME_PREFIX}/${keyInfo.ENT_CD}_${byteLimitContNo}_${keyInfo.CUST_CLEAR_REQ_NO}`,
      fileName: record.FILE_NM,
    };
  });

  const downloadObj = {
    fileList: fileList,
    reqUser: userId,
    dispMenuItem: authority.find((item) => item.SCREEN_ID === screenId)?.MENU_RESOURCE_ID,
  };

  const dlCustNo = Array.from(new Set(downloadFileList.map((fdl: any) => fdl.CUST_CLEAR_REQ_NO)));

  return compressFileDownload(isUpdateDownloadFlg ? { ...downloadObj, dlCustNo } : downloadObj);
};
